import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ButtonComponent,
  FileUploadComponent,
  IconButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  ModalService,
  SkeletonDirective,
  TextAreaComponent,
} from '@ui-lib/components';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { FirmwareManagementService } from '../../services/firmware-management.service';
import {
  FirmwareAction,
  IEquipmentModelSearchRequest,
  IFirmwareFormControls,
  IFirmwareModalData,
  IFirmwareModalPublicData,
} from '../../firmwares.model';
import { FirmwareByIdResponse } from '../../../../core/openapi/bff-admin-api';
import { EquipmentModelSelectorComponent } from '../../../../shared/components/equipment-model-selector/equipment-model-selector.component';
import { nonEmptyArrayValidator } from '../../../../shared/validators/non-empty-array.validator';
import { FirmwaresService } from '../../services/firmwares.service';
import { LoadingState } from '../../../../shared/types/loading.type';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';

@Component({
  selector: 'mon-firmware-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconButtonComponent,
    TranslateModule,
    SkeletonDirective,
    ButtonComponent,
    InputComponent,
    TextAreaComponent,
    FormsModule,
    EquipmentModelSelectorComponent,
    FileUploadComponent,
  ],
  templateUrl: './firmware-modal.component.html',
  styleUrl: './firmware-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FirmwareManagementService, FirmwaresService],
})
export class FirmwareModalComponent
  extends ModalData<IFirmwareModalData>
  implements OnInit, IFirmwareModalPublicData
{
  private readonly fb = inject(FormBuilder);
  private readonly firmwareManagementService = inject(FirmwareManagementService);
  private readonly firmwaresService = inject(FirmwaresService);
  private readonly modalService = inject(ModalService);
  private readonly modalRef = inject<ModalRef<boolean>>(ModalRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  private readonly modelsInterval = 10 as const;

  protected readonly firmwareLoadingState: Signal<LoadingState> =
    this.firmwareManagementService.loadingState;

  protected readonly LoadingState = LoadingState;

  protected readonly isModelListLoading: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly FirmwareAction = FirmwareAction;
  protected readonly canManageFirmware = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FirmwareManage),
    { initialValue: false },
  );
  protected readonly canReadEquipmentModels = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.EquipmentModelRead),
    { initialValue: false },
  );

  protected modelsUpstream$ = new BehaviorSubject<IEquipmentModelSearchRequest>({
    skip: 0,
    take: this.modelsInterval,
    includeTotalCount: true,
  });

  protected readonly firmwareFormGroup: FormGroup<IFirmwareFormControls> = this.fb.group({
    name: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(100)],
      nonNullable: true,
    }),
    version: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(100)],
      nonNullable: true,
    }),
    models: this.fb.control<string[]>([], {
      validators: [nonEmptyArrayValidator()],
      nonNullable: true,
    }),
    comment: this.fb.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(256)],
    }),
    file: this.fb.control<File[] | null>(null, {
      validators: this.isEditingMode ? null : [Validators.required, nonEmptyArrayValidator()],
    }),
  });

  private _formChanged = false;

  protected readonly isDownloadingFirmware = signal<boolean>(false);

  protected serverEntity?: FirmwareByIdResponse;

  protected get selectedFile(): File | null {
    const files = this.getControl<File[] | null>('file').value;
    return files?.[0] ?? null;
  }

  public get formChanged(): boolean {
    return this._formChanged;
  }

  public get invalidFormData(): boolean {
    return this.firmwareFormGroup.invalid;
  }

  public get isReadonly(): boolean {
    return !this.canManageFirmware() || !this.canReadEquipmentModels();
  }

  public get isCreatingMode(): boolean {
    return this.modalData.action === FirmwareAction.Create;
  }

  public get isEditingMode(): boolean {
    return (
      this.modalData.action === FirmwareAction.Edit &&
      this.modalData.firmwareId !== undefined &&
      this.modalData.firmwareId.length > 0
    );
  }

  public ngOnInit(): void {
    this.firmwareFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this._formChanged = true;
      });

    this.modalRef.preventClose(() => {
      if (!this.formChanged) {
        return false;
      }

      const confirmationRef = this.modalService.open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'firmware-closure-confirm-modal',
        data: {
          headerTranslationKey: 'monitoring.firmware.modal.unsaved-changes-title',
          descriptionTranslationKey: 'monitoring.firmware.modal.unsaved-changes-text',
          approvalTranslationKey: 'monitoring.firmware.modal.close',
          cancellationTranslationKey: 'monitoring.firmware.modal.stay',
        },
      });

      return confirmationRef.afterClose$.pipe(
        take(1),
        map((confirmed) => !confirmed),
      );
    });

    if (this.isEditingMode) {
      const fileControl = this.getControl<File[] | null>('file');
      fileControl.clearValidators();
      fileControl.updateValueAndValidity({ emitEvent: false });
    }

    if (this.isEditingMode && typeof this.modalData.firmwareId === 'string') {
      this.firmwareManagementService
        .loadFirmwareById(this.modalData.firmwareId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((firmware: FirmwareByIdResponse): void => {
          this.firmwareFormGroup.patchValue(
            {
              name: firmware.name ?? '',
              version: firmware.version ?? '',
              comment: firmware.comment ?? '',
              models: [...(firmware.equipmentModelIds ?? [])],
            },
            { emitEvent: false },
          );

          this.serverEntity = { ...firmware };
        });
    }
  }

  public onSubmit(): void {
    if (!this.canManageFirmware() || !this.canReadEquipmentModels()) return;

    this.firmwareFormGroup.markAllAsTouched();

    if (this.invalidFormData) return;

    const { name, version, comment, file, models } = this.firmwareFormGroup.getRawValue();

    if (models.length === 0) return;

    let submit$: Observable<void>;

    switch (this.modalData.action) {
      case FirmwareAction.Create: {
        if (!file) throw new Error('File is required');
        submit$ = this.firmwareManagementService.createFirmware(
          name,
          version,
          models,
          file[0],
          comment,
        );
        break;
      }
      case FirmwareAction.Edit: {
        submit$ = this.firmwareManagementService.updateFirmware({
          id: this.serverEntity?.id ?? this.modalData.firmwareId ?? '',
          name,
          version,
          comment,
          equipmentModelIds: models,
        });
        break;
      }
      default: {
        throw new Error('Unsupported action');
      }
    }

    submit$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      this._formChanged = false;
      this.modalRef.close(true);
    });
  }

  public deleteFirmware(): void {
    if (
      !this.canManageFirmware() ||
      !this.canReadEquipmentModels() ||
      this.firmwareLoadingState() === LoadingState.LOADING
    ) {
      return;
    }

    const firmwareName = this.firmwareFormGroup.get('name')?.value || '';

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          headerTranslationKey: 'monitoring.firmware.modal.deletion-warning-title',
          descriptionTranslationKey: 'monitoring.firmware.modal.deletion-warning-text',
          descriptionTranslationParams: { name: firmwareName },
          approvalTranslationKey: 'monitoring.firmware.modal.delete-firmware',
          cancellationTranslationKey: 'monitoring.firmware.modal.abort-deletion',
        },
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        this.firmwareManagementService
          .deleteFirmware(this.serverEntity?.id ?? this.modalData.firmwareId ?? '', firmwareName)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalRef.close(result);
          });
      });
  }

  public getControl<T>(controlName: keyof IFirmwareFormControls): FormControl<T> {
    const control = this.firmwareFormGroup.get(controlName);
    if (!control) {
      throw new Error(`Control '${controlName}' not found in form group`);
    }
    return control as FormControl<T>;
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    const control = this.getControl<File[] | null>('file');

    control.setValue(files.length > 0 ? files : null);
    control.markAsTouched();
    control.updateValueAndValidity();

    if (input) {
      input.value = '';
    }

    this._formChanged = true;
  }

  public downloadUploadedFirmware(): void {
    if (this.isEditingMode && this.serverEntity && !this.isDownloadingFirmware()) {
      this.isDownloadingFirmware.set(true);
      this.firmwaresService
        .downloadFirmware(
          this.serverEntity.id,
          this.serverEntity.name,
          this.serverEntity.originFileName,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          complete: () => this.isDownloadingFirmware.set(false),
        });
    }
  }
}
